import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, BookOpen, Trophy, Settings, ChevronLeft, ChevronRight, Terminal } from 'lucide-react';
import { UserStats } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { GroupSelector } from './GroupSelector';
import { ProgressBar } from '../ui/Progress';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  stats: UserStats;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  selectedGroup: GroupId;
  onSelectGroup: (group: GroupId) => void;
}
export function Sidebar({
  currentView,
  onNavigate,
  stats,
  isCollapsed,
  onToggleCollapse,
  selectedGroup,
  onSelectGroup
}: SidebarProps) {
  const navItems = [{
    id: 'dashboard',
    label: 'Overzicht',
    icon: LayoutDashboard
  }, {
    id: 'modules',
    label: 'Vakken',
    icon: BookOpen
  }, {
    id: 'progress',
    label: 'Voortgang',
    icon: Trophy
  }, {
    id: 'settings',
    label: 'Instellingen',
    icon: Settings
  }, {
    id: 'developer',
    label: 'Developer',
    icon: Terminal,
    isDev: true
  }];
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats.xp % 250 / 250 * 100;
  return <motion.aside className="hidden md:flex flex-col h-screen bg-white border-r border-slate-200 sticky top-0 z-30" initial={false} animate={{
    width: isCollapsed ? 80 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1034">
      {/* Logo Section */}
      <div className="h-20 flex items-center px-6 border-b border-slate-100" data-id="element-1035">
        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shadow-sm shadow-indigo-200 flex-shrink-0" data-id="element-1036">
          <span className="text-white font-bold text-lg" data-id="element-1037">C</span>
        </div>

        <AnimatePresence data-id="element-1038">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-bold text-slate-900 ml-3 text-lg whitespace-nowrap overflow-hidden" data-id="element-1039">
              Cito Meester
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Group Selector */}
      <div className="py-4 border-b border-slate-100" data-id="element-1040">
        <GroupSelector selectedGroup={selectedGroup} onSelect={onSelectGroup} isCollapsed={isCollapsed} data-id="element-1041" />
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-6 px-3 space-y-2 overflow-y-auto" data-id="element-1042">
        {navItems.map(item => {
        const isActive = currentView === item.id;
        return <button key={item.id} onClick={() => onNavigate(item.id)} className={`
                w-full flex items-center px-3 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden
                ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}
                ${item.isDev ? 'mt-8 border-t border-slate-100 pt-4' : ''}
              `} title={isCollapsed ? item.label : undefined} data-id="element-1043">
              {/* Active Indicator Bar */}
              {isActive && <motion.div layoutId="activeIndicatorBar" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-indigo-600 rounded-r-full" data-id="element-1044" />}

              <item.icon className={`w-6 h-6 flex-shrink-0 relative z-10 ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-1045" />

              <AnimatePresence data-id="element-1046">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className="ml-3 whitespace-nowrap overflow-hidden relative z-10" data-id="element-1047">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </button>;
      })}
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-slate-100" data-id="element-1048">
        <div className={`
          flex items-center p-2 rounded-xl bg-slate-50 border border-slate-100
          ${isCollapsed ? 'justify-center' : 'justify-start'}
        `} data-id="element-1049">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm border-2 border-white shadow-sm flex-shrink-0" data-id="element-1050">
            {stats.name.charAt(0)}
          </div>

          <AnimatePresence data-id="element-1051">
            {!isCollapsed && <motion.div initial={{
            opacity: 0,
            width: 0
          }} animate={{
            opacity: 1,
            width: 'auto'
          }} exit={{
            opacity: 0,
            width: 0
          }} className="ml-3 overflow-hidden flex-1 min-w-0" data-id="element-1052">
                <div className="flex justify-between items-center mb-1" data-id="element-1053">
                  <div className="text-sm font-bold text-slate-900 truncate" data-id="element-1054">
                    {stats.name}
                  </div>
                  <div className="text-xs font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-1055">
                    Lvl {stats.level}
                  </div>
                </div>

                {/* XP Progress Bar */}
                <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden" data-id="element-1056">
                  <div className="bg-indigo-500 h-full rounded-full transition-all duration-500" style={{
                width: `${progressToNextLevel}%`
              }} data-id="element-1057" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3 top-24 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors z-40" data-id="element-1058">
        {isCollapsed ? <ChevronRight className="w-3 h-3" data-id="element-1059" /> : <ChevronLeft className="w-3 h-3" data-id="element-1060" />}
      </button>
    </motion.aside>;
}